<template>
    <Modal fullscreen v-model="isOpen" title="" class="no-padding-left no-padding-right no-padding-top no-padding-bottom">
        <ion-content
            :class="{[selectedSegment]: true}"
        >
            <div
                class="content-fixed"
                slot="fixed"

            >
                <ion-segment
                    v-model="selectedSegment"
                >
                    <ion-segment-button :value="AInesJobsiteListType.Favorite">
                    <InlineSVG
                        :src="KapitelIcon.favoriteJobsite"
                    />
                    </ion-segment-button>
                    <ion-segment-button :value="AInesJobsiteListType.Alternative">
                    <InlineSVG
                        :src="KapitelIcon.alternativeJobsite"
                    />
                    </ion-segment-button>
                    <ion-segment-button :value="AInesJobsiteListType.Blacklist">
                    <InlineSVG
                        :src="KapitelIcon.blockedJobsite"
                    />
                    </ion-segment-button>
                </ion-segment>                
                <h2>{{headerLabel}}</h2>
            </div>
            
            <ion-list>

                <JobsiteListItem
                    v-for="item in jobsitesToRender"
                    :jobsite="item.jobsite"
                    :disabled="item.isClientPrevented"
                    :showBlacklistedCategorySubtitle=false
                    @click="onClickJobsite(item)"
                    @info="jobsiteDetailModalData=item.jobsite.id"
                />
            </ion-list>
            <br>

            <ion-action-sheet
                :is-open="isActionModalOpen"
                @didDismiss="onActionModalDismiss"
                :buttons="actionSheetButtons"
            />

            <JobsiteSearchModal
                v-model="jobsiteAddModalIsOpen"
                @select="handleSelectJobsite"
                :alreadySelectedJobsites="alreadySelectedJobsites"
                :title="addTitleLabel"
            />

            <JobsiteDetails v-model="jobsiteDetailModalData" />
        </ion-content>

        <template #actions="{dismiss}">
            <ion-button
                color="light"
                expand="block"
                v-on:click="jobsiteAddModalIsOpen = true"
            >
                Station hinzufügen
            </ion-button>
            <ion-button
                color="primary"
                expand="block"
                @click="dismiss"
            >
                Fertig
            </ion-button>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import {
    IonActionSheet,
    IonButton,
    IonContent,
    IonItem,
    IonLabel,
    IonList,
    IonReorder,
    IonSegment,
    IonSegmentButton
} from '@ionic/vue'
import {computed, PropType, ref, watch} from 'vue'

import Modal from '@/components/Modal.vue'
import JobsiteSearchModal from "@/views/components/JobsiteSearchModal.vue";
import {
    addBlacklistJobsite,
    addFavoriteJobsite,
    addSecondChoiceJobsite,
    fetchBlacklistedJobsites,
    fetchClientPreventedJobsites,
    fetchFavoriteJobsites,
    fetchSecondChoiceJobsites,
    jobsiteListSubtitle,
    jobsiteListTitle,
    removeBlacklistJobsite,
    removeFavoriteJobsite,
    removeSecondChoiceJobsite,
    updateSecondChoiceJobsites
} from "@/helper/jobsite"
import {heatOrReheatExperts} from "@/helper/chat/chatBL";
import InlineSVG from '@/components/InlineSVG.vue'
import JobsiteDetails from "@/views/JobsiteDetails.vue";
import {KapitelIcon} from "@/helper/icon";
import {AInesJobsiteListType, Jobsite} from "@/graphql/generated/graphql";
import JobsiteListItem from "@/views/Settings/JobsiteListItem.vue";

interface JobsiteListItem {
    jobsite: Jobsite
    isClientPrevented: boolean
}

const isOpen = defineModel({type: Boolean})
const jobsiteDetailModalData = ref<number | undefined>(undefined)

const props = defineProps({
    initialSegment: {
        type: String as PropType<AInesJobsiteListType>,
        required: false,
        default: AInesJobsiteListType.Favorite
    }
})

/**
 * Refs
 */


const favJobsites = ref<Jobsite[]>([])
const altJobsites = ref<Jobsite[]>([])
const blockedJobsites = ref<Jobsite[]>([])
const clientPreventedJobsites = ref<Jobsite[]>([])

const jobsiteAddModalIsOpen = ref(false)
const isActionModalOpen = ref(false)
const isSortingActive = ref(false)
const selectedJobsite = ref<JobsiteListItem | undefined>(undefined)
const selectedSegment = ref<AInesJobsiteListType>(props.initialSegment)

/**
 * Computed
 */

const headerLabel = computed(() => {
    switch (selectedSegment.value) {
        case AInesJobsiteListType.Favorite: return  "Lieblingsstationen";
        case AInesJobsiteListType.Alternative: return  "Alternativen";
        case AInesJobsiteListType.Blacklist: return  "Gesperrte Stationen";
    }
    return '';
})

const addTitleLabel = computed(() => {
    switch (selectedSegment.value) {
        case AInesJobsiteListType.Favorite: return  "Neue Lieblingsstation finden";
        case AInesJobsiteListType.Alternative: return  "Neue Alternativstation finden";
        case AInesJobsiteListType.Blacklist: return  "Station sperren";
    }
    return undefined;
})

const actionSheetButtons = computed(() => {
    const buttons: any[] = []
    const jobsite = selectedJobsite.value?.jobsite

    if (!jobsite) {
        return buttons
    }

    buttons.push({
            text: 'Details anzeigen',
            data: 'showDetails',
        })

    if (isJobsiteFavorite(jobsite)) {
        buttons.push({
            text: 'Entfernen',
            data: 'removeFav',
        })
    }
    else {
        buttons.push({
            text: 'Als Lieblingsstation',
            data: 'addFav'
        })
    }

    if (isJobsiteSecondChoice(jobsite)) {
        buttons.push({
            text: 'Entfernen',
            data: 'removeAlt',
        })
    }
    else {
        buttons.push({
            text: 'Als Alternativstation',
            data: 'addAlt'
        })
    }

    if (isJobsiteBlocked(jobsite)) {
        buttons.push({
            text: 'Entsperren',
            data: 'removeBlocked',
        })
    }
    else {
        buttons.push({
            text: 'Sperren',
            role: 'destructive',
            data: 'addBlocked'
        })
    }

    buttons.push({
        text: 'Abbrechen',
        role: 'cancel',
        data: {
            action: 'cancel',
        },
    })

    return buttons
})

const jobsitesToRender = computed<JobsiteListItem[]>(() => {
    const mapJobsitesToListItems = (jobsites: Jobsite[], isClientPrevented = false): JobsiteListItem[] => {
        return jobsites.map(jobsite => ({jobsite, isClientPrevented}))
    }

    switch(selectedSegment.value) {
        case AInesJobsiteListType.Favorite:
            return mapJobsitesToListItems(favJobsites.value)
        case AInesJobsiteListType.Alternative:
            return mapJobsitesToListItems(altJobsites.value)
        case AInesJobsiteListType.Blacklist:
            return [
                ...mapJobsitesToListItems(
                    blockedJobsites.value.filter(jobsite => !isJobsiteClientPrevented(jobsite))
                ),
                ...mapJobsitesToListItems(clientPreventedJobsites.value, true)
            ]
        default:
            throw new Error('invalid segment.')
    }
})

const alreadySelectedJobsites = computed(() => jobsitesToRender.value.map(item => item.jobsite))

/**
 * API
 */


const fetchFavs = () => {
    return fetchFavoriteJobsites().then(res => {
        favJobsites.value = res
    })
}

const fetchAlternatives = () => {
    return fetchSecondChoiceJobsites().then(res => {
        altJobsites.value = res
    })
}

const fetchBlocked = () => {
    return fetchBlacklistedJobsites().then(res => {
        blockedJobsites.value = res
    })
}

const fetchClientPrevented = () => {
    return fetchClientPreventedJobsites().then(res => {
        clientPreventedJobsites.value = res.map((jobsite: Jobsite) => ({...jobsite, isClientPreventedJobsite: true}))
    })
}


const postUpdateJobsites = () => {
    heatOrReheatExperts()
}


const addFav = async (jobsite: Jobsite) => {

    const requestQueue = [];

    altJobsites.value.some(llJobsite => llJobsite.id === jobsite.id)? requestQueue.push(removeAlt): null
    blockedJobsites.value.some(llJobsite => llJobsite.id === jobsite.id)? requestQueue.push(removeBlock): null
    requestQueue.push(addFavoriteJobsite)

    let result;
    for (const request of requestQueue) {
        result = await request(jobsite)
    }

    postUpdateJobsites()

    favJobsites.value = result
}

const addAlt =  async (jobsite: Jobsite) => {

    const requestQueue = [];

    favJobsites.value.some(llJobsite => llJobsite.id === jobsite.id)? requestQueue.push(removeFav): null
    blockedJobsites.value.some(llJobsite => llJobsite.id === jobsite.id)? requestQueue.push(removeBlock): null
    requestQueue.push(addSecondChoiceJobsite)

    let result;
    for (const request of requestQueue) {
        result = await request(jobsite)
    }

    postUpdateJobsites()

    altJobsites.value = result

}

const addBlock = async (jobsite: Jobsite) => {

    const requestQueue = [];

    favJobsites.value.some(llJobsite => llJobsite.id === jobsite.id)? requestQueue.push(removeFav): null
    altJobsites.value.some(llJobsite => llJobsite.id === jobsite.id)? requestQueue.push(removeAlt): null
    requestQueue.push(addBlacklistJobsite)

    let result;
    for (const request of requestQueue) {
        result = await request(jobsite)
    }

    postUpdateJobsites()

    blockedJobsites.value = result

}

const removeFav = (jobsite: Jobsite) => {
    return removeFavoriteJobsite(jobsite).then(res => {
        favJobsites.value = res
    }).then(postUpdateJobsites)
}

const removeAlt = (jobsite: Jobsite) => {
    return removeSecondChoiceJobsite(jobsite).then(res => {
        altJobsites.value = res
    }).then(postUpdateJobsites)
}

const removeBlock = (jobsite: Jobsite) => {
    return removeBlacklistJobsite(jobsite).then(res => {
        blockedJobsites.value = res
    }).then(postUpdateJobsites)
}

const updateAlternativeSorting = () => {
    const ids = altJobsites.value.map(jobsite => jobsite.id)

    return updateSecondChoiceJobsites(ids).then(res => {
        altJobsites.value = res
    })
}

/**
 * Events
 */

const onClickJobsite = (jobsiteListItem: JobsiteListItem) => {
    if (jobsiteListItem.isClientPrevented) {
        return
    }

    selectedJobsite.value = jobsiteListItem
    isActionModalOpen.value = true
}

const onActionModalDismiss = async (event: any) => {
    const jobsite = selectedJobsite.value?.jobsite
    const action = event.detail.data

    isActionModalOpen.value = false
    selectedJobsite.value = undefined

    if (!jobsite) {
        return
    }

    switch(action) {
        case 'addFav':
            if (isJobsiteSecondChoice(jobsite)) {
                await removeAlt(jobsite)
            }
            if (isJobsiteBlocked(jobsite)) {
                await removeBlock(jobsite)
            }
            addFav(jobsite)
            break;

        case 'removeFav':
            removeFav(jobsite)
            break;

        case 'addAlt':
            if (isJobsiteFavorite(jobsite)) {
                await removeFav(jobsite)
            }
            if (isJobsiteBlocked(jobsite)) {
                await removeBlock(jobsite)
            }
            addAlt(jobsite)
            break;

        case 'removeAlt':
            removeAlt(jobsite)
            break;

        case 'addBlocked':
            if (isJobsiteFavorite(jobsite)) {
                await removeFav(jobsite)
            }
            if (isJobsiteSecondChoice(jobsite)) {
                await removeAlt(jobsite)
            }
            addBlock(jobsite)
            break;

        case 'removeBlocked':
            removeBlock(jobsite)
            break;

        case 'showDetails':
            jobsiteDetailModalData.value=jobsite.id
            break;
    }
}

const handleSelectJobsite = (jobsite: Jobsite) => {
    switch(selectedSegment.value) {
        case AInesJobsiteListType.Favorite:
            return addFav(jobsite)
        case AInesJobsiteListType.Alternative:
            return addAlt(jobsite)
        case AInesJobsiteListType.Blacklist:
            return addBlock(jobsite)
        default:
            throw new Error('invalid segment.')
    }
}

// const onReorder = event => {
//     altJobsites.value = event.detail.complete(altJobsites.value)
// }


fetchFavs()
fetchAlternatives()
fetchBlocked()
fetchClientPrevented()

watch(selectedSegment, () => {
    isSortingActive.value = false
})

watch(isSortingActive, value => {
    if (value) {
        return
    }

    updateAlternativeSorting()
})

/**
 * Helper
 */

const isJobsiteFavorite = (jobsite: Jobsite) => favJobsites.value.find(j => j.id === jobsite.id) !== undefined
const isJobsiteSecondChoice = (jobsite: Jobsite) => altJobsites.value.find(j => j.id === jobsite.id) !== undefined
const isJobsiteBlocked = (jobsite: Jobsite) => blockedJobsites.value.find(j => j.id === jobsite.id) !== undefined
const isJobsiteClientPrevented = (jobsite: Jobsite) => clientPreventedJobsites.value.find(j => j.id === jobsite.id) !== undefined
</script>

<style lang="scss" scoped>

ion-list {
     margin-top: 8rem;
}

:deep(.content-fixed) {
    width:100%;
    background-color: var(--ion-color-white); // prevent transparency

    > h2 {
        color: var(--ion-color-black);
    }

    > ion-segment {

        display: flex;
        width:100%;
        align-items: center;


        > ion-segment-button {
            flex-grow: 1;
            justify-content: center;
            align-items: center;
            line-height: 0;

            > .inline-svg {
                width: 1.5rem;
                color: var(--ion-color-primary);
                filter: drop-shadow( 0 0 0.4rem rgba(var(--ion-color-primary-rgb), 0.5));

                &:not(:last-child) {
                    margin-right: 0.25em;
                }
            }

            &.segment-button-checked {

                background-color: var(--ion-color-primary);

                > .inline-svg {
                    color: var(--ion-color-light);
                }
            }
        }
    }

}

.jobsite-manager-action-modal {
    --height: auto;
}

ion-item.jobsite-item-client-prevented {
    --color: var(--ion-color-medium);
}

.client-prevented-hint {
    color: var(--ion-color-medium);
    font-size: 0.875rem;
}
</style>
